import * as yup from 'yup';

import {
  type ApiElements,
  type ApiElementsWithoutEmptyArray,
  COMPANY_QUESTION_COMPONENT,
  type CompanyQuestion,
  type CompanyQuestionApiQuestionObject,
  type CompanyQuestionApiResponse,
  type CompanyQuestionGroup,
  type CompanyQuestionIsRequired,
  type CompanyQuestionSelectionOption,
  type Elements,
  isApiKonjunktionElement,
  isEmptyArrayElement,
  isKonjunktionElement,
  isListBoxElemente,
  isListBoxElementObject,
  isPlausiElementOfArray,
  type ListBoxElementObject,
} from '@/features/CompanyQuestions/types';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { VglError } from '@/services/errorHandler/vglError';
import { isUpperCase } from '@/utils/strings/strings';
import { splitLabelUrls } from '@/utils/linkify/splitLabelUrls';
import get from 'lodash/get';
import { requirementClassMapper } from '@/features/CompanyQuestions/RequirementMapping/requirementClassMapper';
import { DefaultRequirement } from '@/features/CompanyQuestions/RequirementMapping/DefaultRequirement/DefaultRequirement';
import { useGlobalStore } from '@/features/Global/store';
import type { FormConfig, FormFieldConfig } from '@/utils/form';
import type { Component } from 'vue';
import { computed } from 'vue';
import { DsInput, DsSelect } from '@demvsystems/design-components';
import Datepicker from '@/components/form-elements/Datepicker/Datepicker.vue';
import { FORM_LAYOUT } from '@/utils/form/formLayout';
import NumericCheckbox from '@/components/form-elements/NumericCheckbox/NumericCheckbox.vue';
import RadioGroup from '@/components/form-elements/RadioGroup/RadioGroup.vue';
import type { RadioGroupOption } from '@/components/form-elements/RadioGroup/types';
import { useCompanyQuestionStore } from '@/features/CompanyQuestions/store';
import { isRole, Role, ROLE, useClientStore } from '@/features/Clients';
import SectionContainer from '@/components/layout/SectionContainer.vue';
import { SECTION } from '@/components/layout/types';

export const extractQuestionGroup = (
  apiQuestion: CompanyQuestionApiQuestionObject,
): CompanyQuestionGroup => ({
  groupId: apiQuestion.questionsDetail.value.gruppennummer,
  label: apiQuestion.questionsDetail.value.gruppe,
  questions: [],
});

const getValidationDate = (path: unknown[]) => {
  let compareDate = new Date();

  const [compareDateInputPath] = path;

  if (
    typeof compareDateInputPath === 'string' &&
    compareDateInputPath !== 'Today'
  ) {
    const inputValue = get(
      useCompanyQuestionStore().flatValidationInputData,
      compareDateInputPath,
    );

    if (inputValue !== undefined) {
      compareDate = inputValue as Date;
    }
  }

  return compareDate;
};

// Must keep all available validations coming from the inno API (check documentation)
const questionValidationConcepts: {
  [key: string]: (schema: yup.AnySchema, value: unknown[]) => yup.AnySchema;
} = {
  NE: (schema, value) => schema.notOneOf(value),
  MINLENGTH: (schema, value) =>
    (schema as yup.StringSchema).min(value[0] as any),
  MAXLENGTH: (schema, value) =>
    (schema as yup.StringSchema).max(value[0] as any),
  LE: (schema, value) =>
    schema.test(
      'isLessOrEqual',
      () => {
        const fieldLabel = useCompanyQuestionStore().labelByPath.get(
          value[0] as string,
        );
        if (fieldLabel !== undefined) {
          return `Das Datum darf nicht nach ${fieldLabel} liegen`;
        }

        return `Das Datum darf nicht in der Zukunft liegen`;
      },
      (val: Date | undefined): boolean => {
        if (val === undefined) return false;

        return val <= getValidationDate(value);
      },
    ),
  GE: (schema, value) =>
    schema.test(
      'isGreaterOrEqual',
      () => {
        const fieldLabel = useCompanyQuestionStore().labelByPath.get(
          value[0] as string,
        );
        if (fieldLabel !== undefined) {
          return `Das Datum darf nicht vor ${fieldLabel} liegen`;
        }

        return `Das Datum darf nicht in der Vergangenheit liegen`;
      },
      (val: Date | undefined): boolean => {
        if (val === undefined) return false;

        return val >= getValidationDate(value);
      },
    ),
};

export const generateGroupForm = (
  group: CompanyQuestionGroup,
  isRoleRelated = false,
): FormConfig => {
  const totalRequirements: (() => boolean)[] = [];

  const fieldConfigs: FormFieldConfig[] = group.questions.map((q) => {
    let component: Component = DsInput;
    let inputAttrs: Record<string, unknown> = {};

    if (q.component === COMPANY_QUESTION_COMPONENT.NUMBER) {
      inputAttrs = {
        type: 'number',
      };
    } else if (q.component === COMPANY_QUESTION_COMPONENT.DATE) {
      component = Datepicker;
    } else if (q.component === COMPANY_QUESTION_COMPONENT.CHECKBOX) {
      component = NumericCheckbox;
    } else if (q.component === COMPANY_QUESTION_COMPONENT.SELECT) {
      component = DsSelect;
      inputAttrs = {
        data: q.selectOptions,
        required: true,
      };
    } else if (q.component === COMPANY_QUESTION_COMPONENT.RADIO) {
      component = RadioGroup;
      inputAttrs = {
        options: q.radioOptions,
      };
    }

    totalRequirements.push(q.requirements);

    return {
      component,
      path: q.fieldName,
      label: q.label,
      labelLinks: q.labelLinks,
      inputAttrs,
      isRequired: q.required,
      inlineGroup: q.component === COMPANY_QUESTION_COMPONENT.CHECKBOX,
      formGroupClass: FORM_LAYOUT.USE_1_COL,
      renderWhen: computed(() => {
        const store = useCompanyQuestionStore();
        if (q.requirements()) {
          store.forms.add(q.fieldName);
          if (isRoleRelated) {
            store.roleRelatedForms.add(q.fieldName);
          }
          return true;
        }

        store.forms.delete(q.fieldName);
        store.roleRelatedForms.delete(q.fieldName);
        return false;
      }),
    };
  });

  return {
    formName: group.label,
    fieldConfigs: [
      {
        component: SectionContainer,
        inputAttrs: {
          name: SECTION.CONTRACT_COMPANY_QUESTION,
          label: group.label,
          inline: true,
        },
        childWrapperClasses: FORM_LAYOUT.GRID_1_COL,
        fieldConfigs,
        renderWhen: computed(() => {
          let hasActiveQuestions = false;
          totalRequirements.forEach((requirement) => {
            if (requirement()) {
              hasActiveQuestions = true;
            }
          });

          return hasActiveQuestions;
        }),
      },
    ],
    gridClasses: FORM_LAYOUT.GRID_1_COL,
  };
};

// will create a function to verify if a specific company question has to be rendered or not
//  -> true   = display question
//  -> false  = hide question
const createQuestionRequirementSchema = (
  elements: Elements[],
  role: Role,
): (() => boolean) => {
  const requirements: CompanyQuestionIsRequired[] = [];

  elements.forEach((el) => {
    if (isKonjunktionElement(el)) {
      const plausisAsArray = Array.isArray(el.konjunktion.plausi)
        ? el.konjunktion.plausi
        : [el.konjunktion.plausi];
      plausisAsArray.forEach((check) => {
        if (!isUpperCase(check.bedingung) && !isPlausiElementOfArray(check)) {
          const mapper = new (requirementClassMapper[check.bedingung] ??
            DefaultRequirement)(
            check.bedingung,
            Array.isArray(check.value) ? check.value : [check.value],
          );

          requirements.push(() => {
            const currentValue = get(
              useCompanyQuestionStore().flatValidationInputData,
              mapper.getFrontendKey(useGlobalStore().getSparte),
            );

            if (Array.isArray(currentValue)) {
              let found = false;
              currentValue.forEach((currentValueElement) => {
                if (mapper.getValues().includes(currentValueElement)) {
                  found = true;
                }
              });

              return found;
            }

            return mapper.getValues().includes(currentValue);
          });
        }
      });
    }
  });

  if (requirements.length === 0) {
    return () => useClientStore().hasClientWithRole(role);
  }

  return () => {
    let passed = true;

    requirements.forEach((requirement) => {
      if (!requirement()) {
        passed = false;
      }
    });

    if (passed && !useClientStore().hasClientWithRole(role)) {
      passed = false;
    }

    return passed;
  };
};

const createQuestionYupSchema = (
  baseSchema: yup.AnySchema,
  elements: Elements[],
  fieldPath: string,
  isRequired: boolean,
): yup.AnySchema => {
  let validationSchema = baseSchema;

  const allowedArrayValues: string[] = [];

  elements.forEach((el) => {
    if (isKonjunktionElement(el)) {
      const plausisAsArray = Array.isArray(el.konjunktion.plausi)
        ? el.konjunktion.plausi
        : [el.konjunktion.plausi];
      plausisAsArray.forEach((check) => {
        if (isUpperCase(check.bedingung) && check.bedingung !== fieldPath) {
          // yup specific check (like max, min, etc.) - must be defined in questionValidationConcepts
          if (
            typeof questionValidationConcepts[check.bedingung] !== 'function'
          ) {
            throw new VglError(
              VGL_ERROR_CODE.COMPANY_QUESTION_MISSING_VALIDATION_DEFINITION,
              {
                additionalData: {
                  check,
                  fieldPath,
                },
              },
            );
          }

          let val = check.value;

          if (baseSchema.type === 'number') {
            const n = Number(check.value[0]);
            // We have to get the actual max/min value to make yup work with the checks here
            if (check.bedingung === 'MAXLENGTH') {
              val = [(10 ** n - 1).toString()]; // 5 Digits allowed = (10 ^ 5) - 1 = 99999
            } else if (check.bedingung === 'MINLENGTH') {
              val = [(10 ** (n - 1)).toString()]; // 2 Digits allowed = (10 ^ (2-1)) = 10
            }
          } else if (baseSchema.type === 'date') {
            // For dates, we don't need max/min length checks - we validate by comparing dates only
            if (
              check.bedingung === 'MAXLENGTH' ||
              check.bedingung === 'MINLENGTH'
            ) {
              return;
            }
          }

          // Special case for elementargefahren_bemerkungen -> not required, and must be completely optional but has MINLENGTH given -.-
          if (
            !isRequired &&
            check.bedingung === 'MINLENGTH' &&
            val[0] === '1'
          ) {
            validationSchema = validationSchema.optional();
            return;
          }

          validationSchema = questionValidationConcepts[check.bedingung](
            validationSchema,
            val,
          );
        } else if (isPlausiElementOfArray(check)) {
          // must match allowedArrayValues
          validationSchema = validationSchema.oneOf([...allowedArrayValues]);
        }
      });
    } else if (isListBoxElemente(el)) {
      // loop the list box elements to create a set of allowed values
      el.element.forEach((arrEl) => {
        allowedArrayValues.push(arrEl['@attributes'].value);
      });
    }
  });

  if (isRequired && !allowedArrayValues.includes('0')) {
    validationSchema = validationSchema.required().notOneOf(['0']);
  }

  return yup.object({
    [fieldPath]: validationSchema,
  });
};

export const extractQuestion = (
  apiQuestion: CompanyQuestionApiQuestionObject,
  role: Role,
): CompanyQuestion => {
  const {
    art,
    feldbezeichnung,
    beschreibung,
    pflichtfeld,
    elemente,
    default: apiDefault,
    sortierung,
  } = apiQuestion.questionsDetail.value;

  let component = COMPANY_QUESTION_COMPONENT.TEXT;
  let yupBaseSchema: yup.AnySchema = yup.string();

  switch (art) {
    case 'integer':
      component = COMPANY_QUESTION_COMPONENT.NUMBER;
      yupBaseSchema = yup.number();
      break;
    case 'datum':
    case 'date':
      component = COMPANY_QUESTION_COMPONENT.DATE;
      yupBaseSchema = yup.date();
      break;
    case 'listbox':
      component = COMPANY_QUESTION_COMPONENT.SELECT;
      break;
    case 'checkbox':
      component = COMPANY_QUESTION_COMPONENT.CHECKBOX;
      break;
    case 'radio':
      component = COMPANY_QUESTION_COMPONENT.RADIO;
      break;
    default:
      break;
  }

  const selectOptions: CompanyQuestionSelectionOption[] = [];
  const radioOptions: RadioGroupOption[] = [];

  elemente.forEach((elementsObj) => {
    if (isListBoxElemente(elementsObj)) {
      elementsObj.element.forEach((option) => {
        if (component === COMPANY_QUESTION_COMPONENT.RADIO) {
          radioOptions.push({
            label: option['@content'],
            value: option['@attributes'].value,
          });
        } else {
          selectOptions.push({
            label: option['@content'],
            value: option['@attributes'].value,
          });
        }
      });
    }
  });

  const requirements = createQuestionRequirementSchema(elemente, role);

  const required = pflichtfeld === '1';
  const schema = createQuestionYupSchema(
    yupBaseSchema.required(),
    elemente,
    feldbezeichnung,
    required,
  );

  // URLs in der Beschreibung (z. B. Erkrankungslisten) werden vom Text
  // getrennt und separat als Links neben dem Label angezeigt.
  const { text: label, links: labelLinks } = splitLabelUrls(beschreibung);

  return {
    component,
    fieldName: feldbezeichnung,
    label,
    labelLinks,
    required,
    requirements,
    schema,
    selectOptions,
    radioOptions,
    default: Array.isArray(apiDefault) ? undefined : apiDefault,
    position: Number.parseInt(sortierung, 10),
  };
};

export const unifyCompanyQuestions = (
  questions:
    | CompanyQuestionApiQuestionObject<ApiElements>[]
    | CompanyQuestionApiQuestionObject<ApiElements>,
): CompanyQuestionApiQuestionObject<Elements>[] => {
  const unifiedApiQuestions: CompanyQuestionApiQuestionObject<Elements>[] = [];

  const q = Array.isArray(questions) ? questions : [questions];

  q.forEach((apiQuestionObj) => {
    if (!Array.isArray(apiQuestionObj.questionsDetail.value.elemente)) {
      apiQuestionObj.questionsDetail.value.elemente = [
        apiQuestionObj.questionsDetail.value.elemente,
      ];
    }

    apiQuestionObj.questionsDetail.value.elemente.forEach((apiElements) => {
      if (isEmptyArrayElement(apiElements)) {
        return;
      }

      const elements: ApiElementsWithoutEmptyArray = apiElements;

      const isKonkjunktion = isApiKonjunktionElement(elements);
      if (!isKonkjunktion) {
        // apiQuestionObj.questionsDetail.value.art === COMPANY_QUESTION_COMPONENT.RADIO
        if (!isListBoxElemente(elements)) {
          if (typeof elements.element === 'string') {
            elements.element = [elements.element];
          }

          if (Array.isArray(elements.element)) {
            elements.element = elements.element.map((element) => {
              const obj: ListBoxElementObject = {
                '@attributes': {
                  value: element as string,
                },
                '@content': element as string,
              };

              return obj;
            });
          } else if (isListBoxElementObject(elements.element)) {
            elements.element = [elements.element];
          }
        }
      } else if (isKonkjunktion) {
        if (
          elements.konjunktion.plausi === undefined &&
          elements.konjunktion.value === 'ELEMENTOFARRAY'
        ) {
          elements.konjunktion.plausi = [
            {
              value: 'ELEMENTOFARRAY',
              bedingung: apiQuestionObj.questionsDetail.key,
            },
          ];

          delete elements.konjunktion.value;
        }

        if (elements.konjunktion.plausi !== undefined) {
          if (!Array.isArray(elements.konjunktion.plausi)) {
            elements.konjunktion.plausi = [elements.konjunktion.plausi];
          }

          elements.konjunktion.plausi.forEach((plausi) => {
            if (!Array.isArray(plausi.value)) {
              if (plausi.value === 'ELEMENTOFARRAY') {
                plausi.value = ['ELEMENTOFARRAY'];
                plausi.bedingung = apiQuestionObj.questionsDetail.key;
              } else {
                plausi.value = [plausi.value];
              }
            }
          });
        }
      }
    });

    unifiedApiQuestions.push(
      apiQuestionObj as CompanyQuestionApiQuestionObject<Elements>,
    );
  });

  return unifiedApiQuestions;
};

const flattenQuestions = (
  apiQuestions: CompanyQuestionApiQuestionObject<ApiElements>[],
  role: Role = ROLE.VERSICHERUNGSNEHMER,
): CompanyQuestionGroup[] => {
  const questionData: Record<string, CompanyQuestionGroup> = {};

  const unifiedQuestions = unifyCompanyQuestions(apiQuestions);

  unifiedQuestions.forEach((apiQuestionObj) => {
    const groupId = apiQuestionObj.questionsDetail.value.gruppennummer;
    if (!Object.hasOwn(questionData, groupId)) {
      questionData[groupId] = extractQuestionGroup(apiQuestionObj);
    }

    const question = extractQuestion(apiQuestionObj, role);

    questionData[groupId].questions.splice(question.position - 1, 0, question);
  });

  // Make sure we apply the correct sorting
  const flatQuestionData: CompanyQuestionGroup[] = [];
  Object.keys(questionData)
    .sort()
    .forEach((groupKey) => {
      flatQuestionData.push(questionData[groupKey]);
    });

  flatQuestionData.forEach((group) => {
    group.questions = group.questions.sort((a, b) => a.position - b.position);
  });

  return flatQuestionData;
};

export const parseCompanyQuestions = (
  companyQuestionApiRes: CompanyQuestionApiResponse<ApiElements> | null,
): CompanyQuestionGroup[] => {
  if (
    companyQuestionApiRes === null ||
    companyQuestionApiRes.frageAttribute === undefined
  ) {
    return [];
  }

  let apiQuestions: CompanyQuestionGroup[] = [];
  // for UNF we have this special case where the questions come as per VP role - so we flatten the structure here and
  // use a placeholder group which will then be split in the custom unf/CompanyQuestions component
  if (Array.isArray(companyQuestionApiRes.frageAttribute)) {
    companyQuestionApiRes.frageAttribute.forEach((questionBlock) => {
      const roleName =
        questionBlock.person.ID === '1'
          ? ROLE.VERSICHERTE_PERSON
          : ROLE.VERSICHERTE_PERSON + questionBlock.person.ID;

      const placeholderGroup: CompanyQuestionGroup = {
        groupId: roleName,
        questions: [],
        label: roleName,
      };

      apiQuestions = apiQuestions.concat(
        [placeholderGroup],
        flattenQuestions(
          questionBlock.person.keyValue,
          isRole(roleName) ? roleName : undefined,
        ),
      );
    });
  } else {
    apiQuestions = apiQuestions.concat(
      flattenQuestions(companyQuestionApiRes.frageAttribute.person.keyValue),
    );
  }

  return apiQuestions;
};
